import React, { useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import PatientStatusLog from 'components/molecules/admin-panel/forms/patient-status-log';
import PatientHoldingStatusLog from 'components/molecules/admin-panel/forms/patient-holding-status-log';
import PatientCcmStatusLog from 'components/molecules/admin-panel/forms/patient-ccm-status-log';
import PatientBlock from 'components/molecules/patientBlock';
import TimeframePicker from 'components/molecules/oneplaform/timeframe-filter/timeframe-picker';
import dayjs from 'dayjs';
import PatientCcmClaimStatusLog from 'components/molecules/admin-panel/forms/patient-ccm-claim-status-log';
import PatientRpmClaimStatusLog from 'components/molecules/admin-panel/forms/patient-rpm-claim-status-log';
import PatientConsumableLog from 'components/molecules/admin-panel/forms/consumable-log';
import PatientEditorLog from 'components/molecules/admin-panel/forms/patient-editor-log';
import PatientThresholdLog from 'components/molecules/admin-panel/forms/threshold-log';
import PatientDeletedNotesLog from 'components/molecules/admin-panel/forms/notes-log';
import {
  isAdminModule,
  isCcmModule,
  isCombinedModule,
  isRpmModule,
} from 'infrastructure/functions';
import useUser from 'utils/useUser';
import { PlatformEnums, type ModuleType } from 'infrastructure/enums';
import PatientInteractiveStatusLog from 'components/molecules/admin-panel/forms/patient-interactive-status-log';

import s from './styles.module.scss';

import type { DateRange } from 'infrastructure/types';

interface IPatientLogTabsProps {
  patientGuid: string;
  module: ModuleType;
}

const PatientLogTabs: React.FC<IPatientLogTabsProps> = (props) => {
  const { patientGuid, module } = props;

  const { isAdmin } = useUser();

  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [activeTab, setActiveTab] = useState('patientStatusLog');
  const { t } = useTranslation();

  const selectedRange = useMemo<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >(() => {
    if (dateRange) {
      return [dayjs(dateRange[0]), dayjs(dateRange[1])];
    }
    return [null, null];
  }, [dateRange]);

  const handleTimeframeChange = (
    dates: null | (dayjs.Dayjs | null)[],
    _dateStrings: string[],
    _presetKey?: string | null,
  ) => {
    const start = dates?.[0]?.toDate();
    const end = dates?.[1]?.toDate();
    setDateRange(start && end ? [start, end] : undefined);
  };

  const tabs = useMemo(() => {
    const items = [
      {
        key: `patientStatusLog`,
        label: t('labels.patientStatusLog'),
        children: (
          <PatientStatusLog patientGuid={patientGuid} dateRange={dateRange} />
        ),
      },
      {
        key: `rpmInteractionStatusLog`,
        label: t('labels.rpmInteractionStatusLog'),
        children: (
          <PatientInteractiveStatusLog
            patientGuid={patientGuid}
            dateRange={dateRange}
            module={PlatformEnums.RPM}
          />
        ),
      },
      {
        key: `ccmInteractionStatusLog`,
        label: t('labels.ccmInteractionStatusLog'),
        children: (
          <PatientInteractiveStatusLog
            patientGuid={patientGuid}
            dateRange={dateRange}
            module={PlatformEnums.CCM}
          />
        ),
      },
      {
        key: `rpmClaimStatusLog`,
        label: t('labels.rpmClaimStatusLog'),
        children: (
          <PatientRpmClaimStatusLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      },
      {
        key: `patientCcmStatusLog`,
        label: t('labels.patientCcmStatusLog'),
        children: (
          <PatientCcmStatusLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      },
      {
        key: `ccmClaimStatusLog`,
        label: t('labels.ccmClaimStatusLog'),
        children: (
          <PatientCcmClaimStatusLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      },
      {
        key: `holdingStatusLog`,
        label: t('labels.holdingStatusLog'),
        children: (
          <PatientHoldingStatusLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      },
    ];

    if (isAdminModule(module)) {
      items.push({
        key: `historyLog`,
        label: t('labels.historyLog'),
        children: (
          <PatientEditorLog patientGuid={patientGuid} dateRange={dateRange} />
        ),
      });
    }
    if (isCombinedModule(module) || isRpmModule(module)) {
      items.push({
        key: `consumablesLog`,
        label: t('labels.consumablesLog'),
        children: (
          <PatientConsumableLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      });
    }
    if (!isCcmModule(module)) {
      items.push({
        key: `thresholdLog`,
        label: t('labels.thresholdsLog'),
        children: (
          <PatientThresholdLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      });
    }

    if (isAdmin) {
      items.push({
        key: `noteActionLog`,
        label: t('labels.noteActionLog'),
        children: (
          <PatientDeletedNotesLog
            patientGuid={patientGuid}
            dateRange={dateRange}
          />
        ),
      });
    }
    return items;
  }, [dateRange]);

  return (
    <PatientBlock
      title={t('labels.patientLogs')}
      canDelete={false}
      canEdit={false}
      headerAddon={
        <div className={s['date-picker']}>
          <TimeframePicker
            selectedRange={selectedRange}
            onRangeChange={handleTimeframeChange}
            minRangeDays={0}
            clearSelection={() => {
              setDateRange(undefined);
            }}
          />
        </div>
      }
    >
      <div className={s.wrapper}>
        <Tabs
          tabPosition="left"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabs}
          className={s.tabs}
        />
      </div>
    </PatientBlock>
  );
};

export default PatientLogTabs;
